import Tick from "@pqina/flip";
import React, { useEffect, useRef, useState } from "react";
import { ReturnValue, useCountUp } from "use-count-up";

import { usePreviousAttributeValue } from "@utils/attributes";
import { CounterTheme } from "@utils/enums";
import { debounce } from "@utils/general";

import Config, { TextSize } from "./boxCounter.config";
import "./boxCounter.scss";

type FlipProps = {
    value: string;
    textSize: TextSize;
    length: number;
    theme: string;
};

type BoxCounterProps = {
    length: number;
    currentValue: number;
    textSize: TextSize;
    theme: CounterTheme;
};

const Flip = ({ value, textSize, length, theme }: FlipProps) => {
    const divRef = useRef<HTMLDivElement | null>(null);
    const tickRef = useRef<any>();

    useEffect(() => {
        const currDiv = divRef.current;
        const didInit = (tick: any) => {
            tickRef.current = tick;
        };
        Tick.DOM.create(currDiv, {
            value,
            didInit,
            repeat: true,
            view: {
                children: [
                    {
                        root: "div",
                        style: ".tick",
                        // layout: "horizontal fill",
                        repeat: true,
                        children: [
                            {
                                view: "flip",
                            },
                        ],
                    },
                ],
            },
        });
        const tickValue = tickRef.current;
        return () => Tick.DOM.destroy(tickValue);
    });

    useEffect(() => {
        calculateFontSize();

        const debouncedCalculateFontSize = debounce(() => {
            calculateFontSize();
        }, 50);

        window.addEventListener("resize", debouncedCalculateFontSize);
        return () => {
            window.removeEventListener("resize", debouncedCalculateFontSize);
        };
    }, []);

    useEffect(() => {
        if (tickRef.current) {
            tickRef.current.value = value;
        }
    }, [value]);

    const calculateFontSize = () => {
        if (divRef.current) {
            const parentElement = divRef.current.closest<HTMLDivElement>(
                ".box-banner--container",
            );
            if (parentElement) {
                const { textWidth } =
                    Config[textSize] || Config[TextSize.MEDIUM];
                const parentWidth = parentElement.offsetWidth;
                divRef.current.style.fontSize = `calc((${parentWidth}px * ${textWidth}) / ${length})`;
            } else {
                if (
                    !divRef.current.classList.contains(
                        `box-counter-${textSize}`,
                    )
                ) {
                    divRef.current.classList.add(`box-counter-${textSize}`);
                }
            }
        }
    };

    return <div className={`box-counter box-counter--${theme}`} ref={divRef} />;
};

const BoxCounter = ({
    length,
    currentValue,
    textSize,
    theme,
}: BoxCounterProps) => {
    const prevAmount = usePreviousAttributeValue({ currentValue });
    const [start, setStart] = useState(currentValue);
    const [end, setEnd] = useState(currentValue);

    const { value, reset } = useCountUp({
        isCounting: true,
        start: start,
        end: end,
    });

    useEffect(() => {
        setEnd(currentValue);
        setStart(prevAmount?.currentValue);
        reset();
    }, [currentValue]);

    const addLeadingZeros = (num: ReturnValue) =>
        String(num).padStart(length, "0");

    return (
        <Flip
            value={addLeadingZeros(value)}
            textSize={textSize}
            length={length}
            theme={theme}
        />
    );
};

export default BoxCounter;
